import { Select, VisuallyHidden } from '@planetra/ui';
import { useId } from 'react';
import { LOCALES, LOCALE_LABELS, isLocale } from '../config';
import { useLocale } from '../use-locale';
import { useT } from '../use-t';

export interface LocaleSwitcherProps {
  className?: string | undefined;
  /**
   * Show the "Language" label beside the control instead of only exposing it
   * to assistive technology. The account menu has room for it; a header does
   * not.
   */
  showLabel?: boolean | undefined;
  /** Radix portals the listbox to document.body. Pass `pl-on-map` when the
   *  switcher sits in map chrome. */
  portalClassName?: string | undefined;
}

const OPTIONS = LOCALES.map((locale) => ({ value: locale, label: LOCALE_LABELS[locale] }));

/**
 * Language picker.
 *
 * Lives here rather than in `@planetra/ui` because it is the one control that
 * genuinely needs to know about locales — and `packages/ui` must stay free of
 * an i18n dependency so it can be prerendered by the landing site and reused
 * by an admin app that may ship English only.
 *
 * Each option is written in its own language: a picker that offers "Malay" to
 * someone who only reads Malay has failed at the one thing it does.
 */
export function LocaleSwitcher({
  className,
  showLabel = false,
  portalClassName,
}: LocaleSwitcherProps) {
  const { t } = useT();
  const { locale, setLocale } = useLocale();
  const labelId = useId();

  const label = t('common:language.label');

  return (
    <div className={className}>
      {showLabel ? (
        <span id={labelId} className="mb-1.5 block text-sm font-medium text-fg">
          {label}
        </span>
      ) : (
        <VisuallyHidden id={labelId}>{label}</VisuallyHidden>
      )}

      <Select
        aria-labelledby={labelId}
        options={OPTIONS}
        value={locale}
        onValueChange={(next) => {
          // The Select's value type is a bare string; narrowing here keeps an
          // impossible value from reaching a store that persists it.
          if (isLocale(next)) setLocale(next);
        }}
        {...(portalClassName === undefined ? {} : { portalClassName })}
      />
    </div>
  );
}
